import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';

export type TabPanelVariant = 'line' | 'button' | 'underline';

/**
 * A single vertical tab item with a trigger and content panel.
 *
 * The trigger displays the tab label and an optional count badge. The content
 * panel is shown when the element is activated via the `active` property.
 * Use inside an `asc-vertical-tabs` widget for full tab group behavior —
 * single-active enforcement and keyboard roving focus. For mobile or compact
 * layouts, pair with `asc-dropdown-tabs`.
 *
 * Supports three visual variants: `line` (left-edge accent border on the active
 * tab), `button` (elevated, rounded active tab), and `underline` (bottom-edge
 * accent border, used by `asc-horizontal-tabs`).
 *
 * @element asc-tab-panel
 *
 * @fires asc-activate - Fired when the trigger is clicked (and not disabled). Consumer must set `active = true`. Detail: `{}`
 *
 * @slot tab-name - Required. The tab label. Phrasing content only.
 * @slot tab-description - Optional description text rendered below the tab name. Text content only; visible in vertical tab orientation.
 * @slot count - Optional count badge rendered as a pill next to the label.
 * @slot panel - The tab panel content shown when `active`. Accepts any HTML.
 *
 * @csspart base - Root `<div>`.
 * @csspart trigger - The `<button>` tab trigger.
 * @csspart label - Wraps the `tab-name` and `tab-description` slots.
 * @csspart tab-description - Wraps the `tab-description` slot; hidden when empty.
 * @csspart count - Wraps the `count` slot; hidden when empty.
 * @csspart panel - The tab panel content container.
 *
 * @cssprop --asc-tab-panel-active-line-color - Active accent border color (line variant). Default: `var(--asc-fg-brand-primary)`
 * @cssprop --asc-tab-panel-active-line-width - Active accent line width. Default: `2px`
 * @cssprop --asc-tab-panel-active-bg - Active trigger background (line variant). Default: `var(--asc-bg-brand-primary)`
 * @cssprop --asc-tab-panel-active-text-color - Active trigger text color (line variant). Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-tab-panel-btn-active-bg - Active trigger background (button variant). Default: `var(--asc-bg-secondary)`
 * @cssprop --asc-tab-panel-btn-active-border-color - Active trigger border color (button variant). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-tab-panel-btn-border-radius - Active trigger border radius (button variant). Default: `var(--asc-radius-xs)`
 * @cssprop --asc-tab-panel-btn-active-text-color - Active trigger text color (button variant). Default: `var(--asc-text-primary)`
 * @cssprop --asc-tab-panel-text-color - Default trigger text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-tab-panel-trigger-bg-hover - Trigger hover background. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-tab-panel-padding-x - Trigger horizontal padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-tab-panel-padding-y - Trigger vertical padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-tab-panel-disabled-text-color - Disabled text color. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-tab-panel-count-bg - Count badge background. Default: `var(--asc-bg-secondary)`
 * @cssprop --asc-tab-panel-count-text-color - Count badge text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-tab-panel-count-active-bg - Count badge background when active. Default: `var(--asc-bg-brand-secondary)`
 * @cssprop --asc-tab-panel-count-active-text-color - Count badge active text color. Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-tab-panel-count-border-radius - Count badge border radius. Default: `var(--asc-radius-full)`
 * @cssprop --asc-tab-panel-content-padding-x - Content panel horizontal padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-tab-panel-content-padding-y - Content panel vertical padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-tab-panel-description-text-color - Description text color. Default: `var(--asc-text-tertiary)`
 */
@customElement('asc-tab-panel')
export class AscTabPanel extends LitElement {
  static override styles = css`
    :host {
      display: block;
      /* Override asc-section block padding when nested inside a tab panel */
      --asc-section-padding-block: var(--asc-spacing-3xl);
    }

    /* In managed mode the widget controls layout */
    :host([managed]) {
      display: contents;
    }

    /* ── Base wrapper ─────────────────────────────────────────── */
    [part='base'] {
      display: flex;
      flex-direction: column;
    }

    :host([managed]) [part='base'] {
      display: block;
      width: 100%;
    }

    /* ── Trigger button ───────────────────────────────────────── */
    [part='trigger'] {
      display: flex;
      align-items: center;
      gap: var(--asc-spacing-md);
      width: 100%;
      padding:
        var(--asc-tab-panel-padding-y, var(--asc-spacing-lg))
        var(--asc-tab-panel-padding-x, var(--asc-spacing-xl));
      background: none;
      border: none;
      border-left: var(--asc-tab-panel-active-line-width, 2px) solid transparent;
      cursor: pointer;
      color: var(--asc-tab-panel-text-color, var(--asc-text-tertiary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-sm);
      text-align: left;
    }

    [part='trigger']:hover:not(:disabled) {
      background-color: var(
        --asc-tab-panel-trigger-bg-hover,
        var(--asc-bg-primary_hover)
      );
    }

    [part='trigger']:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* ── Active state — line variant ──────────────────────────── */
    :host([active]) [part='trigger'] {
      border-left-color: var(
        --asc-tab-panel-active-line-color,
        var(--asc-fg-brand-primary)
      );
      background-color: var(--asc-tab-panel-active-bg, var(--asc-bg-brand-primary));
      color: var(--asc-tab-panel-active-text-color, var(--asc-text-brand-secondary));
    }

    /* ── Button variant ───────────────────────────────────────── */
    :host([variant='button']) [part='trigger'] {
      border-left: none;
      border-radius: var(--asc-tab-panel-btn-border-radius, var(--asc-radius-sm));
      overflow: hidden;
    }

    :host([variant='button'][active]) [part='trigger'] {
      background-color: var(--asc-tab-panel-btn-active-bg, var(--asc-bg-primary));
      color: var(--asc-tab-panel-btn-active-text-color, var(--asc-text-primary));
      box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
    }

    /* ── Underline variant ────────────────────────────────────── */
    :host([variant='underline']) [part='trigger'] {
      border-left: none;
      border-bottom: var(--asc-tab-panel-active-line-width, 2px) solid transparent;
    }

    :host([variant='underline'][active]) [part='trigger'] {
      border-left-color: transparent;
      border-bottom-color: var(
        --asc-tab-panel-active-line-color,
        var(--asc-fg-brand-primary)
      );
      background-color: var(--asc-tab-panel-active-bg, transparent);
      color: var(--asc-tab-panel-active-text-color, var(--asc-text-brand-secondary));
    }

    /* ── Disabled state ───────────────────────────────────────── */
    :host([disabled]) [part='trigger'] {
      cursor: not-allowed;
      color: var(--asc-tab-panel-disabled-text-color, var(--asc-text-disabled));
    }

    :host([disabled]) [part='trigger']:hover {
      background: none;
    }

    /* ── Label ────────────────────────────────────────────────── */
    [part='label'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-spacing-xs);
      flex: 1 0 0;
      min-width: 0;
    }

    /* ── Description ─────────────────────────────────────────── */
    [part='tab-description'] {
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-regular);
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-tab-panel-description-text-color, var(--asc-text-tertiary));
    }

    [part='tab-description'][hidden] {
      display: none;
    }

    /* ── Count badge ──────────────────────────────────────────── */
    [part='count'] {
      display: inline-flex;
      align-items: center;
      padding: var(--asc-spacing-xxs) var(--asc-spacing-md);
      background-color: var(--asc-tab-panel-count-bg, var(--asc-bg-primary));
      color: var(--asc-tab-panel-count-text-color, var(--asc-text-primary));
      border: var(--asc-border-width) solid var(--asc-border-primary);
      border-radius: var(--asc-tab-panel-count-border-radius, var(--asc-radius-full));
      font-size: var(--asc-font-size-text-xs);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-xs);
      flex-shrink: 0;
    }

    [part='count'][hidden] {
      display: none;
    }

    :host([active]) [part='count'] {
      background-color: var(
        --asc-tab-panel-count-active-bg,
        var(--asc-bg-brand-secondary)
      );
      color: var(
        --asc-tab-panel-count-active-text-color,
        var(--asc-text-brand-secondary)
      );
      border-color: transparent;
    }

    /* ── Panel content ────────────────────────────────────────── */
    [part='panel'] {
      /* Reduce asc-section block padding when nested inside a panel */
      --asc-section-padding-block: var(--asc-spacing-3xl);
      padding:
        var(--asc-tab-panel-content-padding-y, var(--asc-spacing-lg))
        var(--asc-tab-panel-content-padding-x, var(--asc-spacing-xl));
    }

    [part='panel'][inert] {
      max-height: 0;
      padding-top: 0;
      padding-bottom: 0;
      overflow: hidden;
    }

    /* ── Managed mode: hide trigger ───────────────────────────── */
    :host([managed]) [part='trigger'] {
      display: none;
    }

    /* ── Align items to top when description is present ─────────── */
    :host([has-description]) [part='trigger'] {
      align-items: flex-start;
    }
  `;

  private readonly _uid = generateUid();

  /** The visual style of the tab trigger. */
  @property({ type: String, reflect: true }) variant: TabPanelVariant = 'line';

  /** Whether this is the currently selected tab. */
  @property({ type: Boolean, reflect: true }) active = false;

  /** Prevents user selection. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Set by `asc-vertical-tabs` widget to indicate the widget is rendering the tab triggers. When `true`, `[part="trigger"]` is hidden. */
  @property({ type: Boolean, reflect: true }) managed = false;

  /** Accessible label forwarded to the internal `[part="panel"]` when in managed mode. */
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;

  @state() private _hasCount = false;
  @state() private _hasDescription = false;

  private _slotHasMeaningfulContent(slot: HTMLSlotElement): boolean {
    return slot.assignedNodes({ flatten: true }).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        return element.childElementCount > 0
          || (element.textContent?.trim().length ?? 0) > 0;
      }
      return (node.textContent?.trim().length ?? 0) > 0;
    });
  }

  private _handleDescriptionSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasDescription = this._slotHasMeaningfulContent(slot);
    this.toggleAttribute('has-description', this._hasDescription);
  }

  private _handleCountSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasCount = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleTriggerClick(): void {
    if (this.disabled) return;
    dispatch(this, 'asc-activate', {});
  }

  override render() {
    return html`
      <div part="base">
        <button
          type="button"
          part="trigger"
          role="tab"
          id="${this._uid}-trigger"
          aria-selected="${this.active}"
          aria-controls="${this._uid}-panel"
          aria-disabled="${this.disabled}"
          ?disabled="${this.disabled}"
          @click="${this._handleTriggerClick}"
        >
          <span part="label">
            <slot name="tab-name"></slot>
            <span part="tab-description" ?hidden="${!this._hasDescription}">
              <slot name="tab-description" @slotchange="${this._handleDescriptionSlotChange}"></slot>
            </span>
          </span>
          <span part="count" ?hidden="${!this._hasCount}" aria-hidden="true">
            <slot name="count" @slotchange="${this._handleCountSlotChange}"></slot>
          </span>
        </button>
        <div
          part="panel"
          role="tabpanel"
          id="${this._uid}-panel"
          aria-labelledby=${!this.managed ? `${this._uid}-trigger` : nothing}
          aria-label=${this.ariaLabel || nothing}
          ?inert="${!this.active}"
        >
          <slot name="panel"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-tab-panel': AscTabPanel;
  }
}
